import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as Yup from 'yup'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import CircularProgress from '@material-ui/core/CircularProgress'
import Link from '@material-ui/core/Link'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import useStyles from './styles'
import { HOLE_IN_ONE_BY_CLAIM_TOKEN, CLAIM_HOLE_IN_ONE } from './query'
import useAchievementsAuth from 'lib/achievements-auth/useAchievementsAuth'
import { decodeToken } from 'lib/achievements-auth/token'
import { ReactComponent as PgaLogo } from 'images/pga-seal-icon.svg'

const formatDate = dateString => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

const validationSchema = Yup.object().shape({
  hasEmailOnRecord: Yup.bool().required(),
  golferEmail: Yup.string().when('hasEmailOnRecord', {
    is: false,
    then: Yup.string()
      .email('Please enter a valid email address')
      .required('Email address is required'),
    otherwise: Yup.string().nullable(),
  }),
  legalAgreementsAccepted: Yup.bool().oneOf(
    [true],
    'You must accept the legal agreements to continue.',
  ),
  marketingOptIn: Yup.bool(),
})

const AchievementsClaimPage = () => {
  const classes = useStyles()
  const location = useLocation()
  const history = useHistory()
  const { login } = useAchievementsAuth()
  const statusRef = useRef(null)

  const searchParams = new URLSearchParams(location.search)
  const claimToken = searchParams.get('token')

  const [validationError, setValidationError] = useState('')
  const [silentReauthError, setSilentReauthError] = useState('')
  const [isSilentReauthLoading, setIsSilentReauthLoading] = useState(false)
  const [silentReauthStarted, setSilentReauthStarted] = useState(false)

  const { data, loading, error } = useQuery(HOLE_IN_ONE_BY_CLAIM_TOKEN, {
    variables: { claimToken },
    skip: !claimToken,
    context: { skipAuth: true },
  })

  const holeInOne = data?.holeInOneByClaimToken
  const hasEmailOnRecord = Boolean(holeInOne?.maskedEmail)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    errors,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      hasEmailOnRecord: false,
      golferEmail: '',
      legalAgreementsAccepted: false,
      marketingOptIn: false,
    },
  })

  const [claimHoleInOne, { loading: claimLoading }] = useMutation(
    CLAIM_HOLE_IN_ONE,
  )

  useEffect(() => {
    setValue('hasEmailOnRecord', hasEmailOnRecord)
  }, [hasEmailOnRecord, setValue])

  useEffect(() => {
    if (statusRef.current) {
      statusRef.current.focus()
    }
  }, [loading, error, holeInOne, isSilentReauthLoading, silentReauthError])

  useEffect(() => {
    if (
      !holeInOne?.legalAgreementsAcceptedAt ||
      silentReauthStarted ||
      !claimToken
    ) {
      return
    }

    let isMounted = true
    const silentReauthenticate = async () => {
      setSilentReauthStarted(true)
      setSilentReauthError('')
      setValidationError('')
      setIsSilentReauthLoading(true)

      try {
        const response = await claimHoleInOne({
          variables: {
            input: {
              claimToken,
              legalAgreementsAccepted: false,
            },
          },
          context: { skipAuth: true },
        })

        const claimResponse = response?.data?.claimHoleInOne
        const token = claimResponse?.token
        const claimedHoleInOne = claimResponse?.holeInOne

        if (!claimResponse?.success || !token || !claimedHoleInOne?.id) {
          if (isMounted) {
            setSilentReauthError(
              claimResponse?.message || 'Unable to sign you in automatically.',
            )
            setIsSilentReauthLoading(false)
          }
          return
        }

        const decoded = decodeToken(token)
        const user = {
          email: decoded?.email,
          name: decoded?.name,
        }

        login(token, user)
        history.push(`/achievements/${claimedHoleInOne.id}`)
      } catch (err) {
        if (isMounted) {
          setSilentReauthError(
            err.message || 'Unable to sign you in automatically.',
          )
          setIsSilentReauthLoading(false)
        }
      }
    }

    silentReauthenticate()

    return () => {
      isMounted = false
    }
  }, [
    claimHoleInOne,
    claimToken,
    history,
    holeInOne,
    login,
    silentReauthStarted,
  ])

  const handleClaimSubmit = async formData => {
    setValidationError('')
    setSilentReauthError('')

    const input = {
      claimToken,
      legalAgreementsAccepted: formData.legalAgreementsAccepted,
      marketingOptIn: formData.marketingOptIn,
    }

    if (!hasEmailOnRecord) {
      input.golferEmail = formData.golferEmail
    }

    try {
      const response = await claimHoleInOne({
        variables: { input },
        context: { skipAuth: true },
      })

      const claimResponse = response?.data?.claimHoleInOne
      const token = claimResponse?.token
      const claimedHoleInOne = claimResponse?.holeInOne

      if (!claimResponse?.success || !token || !claimedHoleInOne?.id) {
        setValidationError(
          claimResponse?.message || 'Failed to claim achievement.',
        )
        return
      }

      const decoded = decodeToken(token)
      const user = {
        email: decoded?.email,
        name: decoded?.name,
      }

      login(token, user)
      history.push(`/achievements/${claimedHoleInOne.id}`)
    } catch (err) {
      setValidationError(err.message || 'Failed to claim achievement.')
    }
  }

  const isLoading = claimLoading || isSubmitting

  const renderAchievementPreview = () => {
    if (!holeInOne) return null

    return (
      <Box className={classes.achievementPreview}>
        <Typography className={classes.achievementPreviewTitle}>
          Your Hole-in-One Achievement
        </Typography>
        <Box className={classes.achievementPreviewGrid}>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Course
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              {holeInOne.courseName || 'N/A'}
            </Typography>
          </Box>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Date
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              {formatDate(holeInOne.occurredAt)}
            </Typography>
          </Box>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Hole
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              #{holeInOne.holeNumber}
            </Typography>
          </Box>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Par
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              {holeInOne.holePar}
            </Typography>
          </Box>
          <Box className={classes.achievementPreviewItem}>
            <Typography className={classes.achievementPreviewLabel}>
              Distance
            </Typography>
            <Typography className={classes.achievementPreviewValue}>
              {holeInOne.yardage} Yds
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const renderLoginPath = () => (
    <Typography className={classes.helpLink}>
      Return to{' '}
      <Link
        href="#"
        className={classes.supportLink}
        onClick={e => {
          e.preventDefault()
          history.push('/achievements/login')
        }}
      >
        achievements login
      </Link>
      .
    </Typography>
  )

  const renderFormError = () =>
    validationError ? (
      <Typography className={classes.validationError}>
        {validationError}
      </Typography>
    ) : null

  const renderConsentSection = () => (
    <>
      <Typography className={classes.sectionLabel}>
        CONSENTS & AGREEMENTS
      </Typography>
      <Box className={classes.consentSection}>
        <Box className={classes.consentBox}>
          <FormControlLabel
            className={classes.consentCheckbox}
            control={
              <Controller
                name="legalAgreementsAccepted"
                control={control}
                render={props => (
                  <Checkbox
                    checked={Boolean(props.value)}
                    onChange={e => {
                      props.onChange(e.target.checked)
                      if (e.target.checked) {
                        setValidationError('')
                      }
                    }}
                    className={classes.checkbox}
                  />
                )}
              />
            }
            label={
              <Box className={classes.consentLabelContainer}>
                <Typography className={classes.consentTitle}>
                  Legal Agreements
                  <span className={classes.requiredAsterisk}>*</span>
                </Typography>
                <Typography className={classes.consentText}>
                  By checking this box, you are agreeing to PGA of America's{' '}
                  <Link
                    href="https://www.pga.com/pga-of-america/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.consentLink}
                  >
                    Terms of Service
                  </Link>
                  , and{' '}
                  <Link
                    href="https://www.pga.com/pga-of-america/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.consentLink}
                  >
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              </Box>
            }
          />
        </Box>

        <Box className={classes.consentBox}>
          <FormControlLabel
            className={classes.consentCheckbox}
            control={
              <Controller
                name="marketingOptIn"
                control={control}
                render={props => (
                  <Checkbox
                    checked={Boolean(props.value)}
                    onChange={e => props.onChange(e.target.checked)}
                    className={classes.checkbox}
                  />
                )}
              />
            }
            label={
              <Box className={classes.consentLabelContainer}>
                <Typography className={classes.consentTitle}>
                  Join the PGA of America's email community to hear about local
                  golf programs, ticketing and volunteer opportunities, swing
                  tips, exclusive offers and more! (Optional)
                </Typography>
                <Typography className={classes.consentText}>
                  By checking this box, I confirm my request to be contacted by
                  email by The Professional Golfers' Association of America
                  ("PGA"), PGA Tournament Corporation, Inc. ("PTC") and/or PGA
                  of America partners. I acknowledge that I can withdraw my
                  consent at any time by following the 'unsubscribe'
                  instructions provided on any communication I receive from this
                  service.
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>
    </>
  )

  const renderSubmitButton = () => (
    <>
      <Button
        type="submit"
        variant="contained"
        fullWidth
        className={classes.submitButton}
        disabled={isLoading}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'CLAIM MY ACHIEVEMENT'
        )}
      </Button>
      <Typography className={classes.helpLink}>
        Need help?{' '}
        <Link href="mailto:support@pga.com" className={classes.supportLink}>
          Contact Support
        </Link>
      </Typography>
      {renderLoginPath()}
    </>
  )

  const renderKnownEmailFlow = () => (
    <form onSubmit={handleSubmit(handleClaimSubmit)}>
      <input type="hidden" name="hasEmailOnRecord" ref={register} />
      <Typography className={classes.title}>Claim Your Ace</Typography>
      <Typography className={classes.description}>
        We found a verified Hole-in-One record associated with your email.
        Review the details and accept the agreements below to claim this
        achievement.
      </Typography>

      {renderAchievementPreview()}

      <Typography className={classes.sectionLabel}>VERIFIED ACCOUNT</Typography>
      <Box className={classes.emailVerificationBox}>
        <Avatar className={classes.emailAvatar}>
          <PersonOutlineIcon />
        </Avatar>
        <Typography className={classes.emailText}>
          {holeInOne.maskedEmail}
        </Typography>
        <CheckCircleIcon className={classes.verifiedIcon} />
      </Box>

      {renderConsentSection()}
      {errors.legalAgreementsAccepted?.message && (
        <Typography className={classes.validationError}>
          {errors.legalAgreementsAccepted.message}
        </Typography>
      )}
      {renderFormError()}
      {renderSubmitButton()}
    </form>
  )

  const renderQRScanFlow = () => (
    <form onSubmit={handleSubmit(handleClaimSubmit)}>
      <input type="hidden" name="hasEmailOnRecord" ref={register} />
      <Typography className={classes.title}>Claim Your Ace</Typography>
      <Typography className={classes.description}>
        A PGA Professional has recorded a Hole-in-One achievement for you. Enter
        your email and accept the agreements below to claim it.
      </Typography>

      {renderAchievementPreview()}

      <Box className={classes.emailInputSection}>
        <Typography className={classes.emailInputLabel}>
          EMAIL ADDRESS
        </Typography>
        <TextField
          name="golferEmail"
          type="email"
          placeholder="you@example.com"
          variant="outlined"
          fullWidth
          inputRef={register}
          error={!!errors.golferEmail}
          helperText={errors.golferEmail?.message}
          className={classes.emailTextField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineIcon className={classes.emailIcon} />
              </InputAdornment>
            ),
          }}
          disabled={isLoading}
        />
      </Box>

      {renderConsentSection()}
      {errors.legalAgreementsAccepted?.message && (
        <Typography className={classes.validationError}>
          {errors.legalAgreementsAccepted.message}
        </Typography>
      )}
      {renderFormError()}
      {renderSubmitButton()}
    </form>
  )

  const renderTokenError = (title, description) => (
    <Box className={classes.errorContainer} tabIndex={-1} ref={statusRef}>
      <ErrorOutlineIcon className={classes.errorIcon} />
      <Typography className={classes.errorTitle}>{title}</Typography>
      <Typography className={classes.errorDescription}>
        {description}
      </Typography>
      <Button
        variant="contained"
        className={classes.errorButton}
        onClick={() => history.push('/achievements/login')}
      >
        GO TO LOGIN
      </Button>
      {renderLoginPath()}
    </Box>
  )

  const renderContent = () => {
    if (loading || isSilentReauthLoading) {
      return (
        <Box className={classes.loadingContainer} tabIndex={-1} ref={statusRef}>
          <CircularProgress size={40} />
          <Typography className={classes.loadingText}>
            {isSilentReauthLoading
              ? 'Signing you in...'
              : 'Loading your ace details...'}
          </Typography>
        </Box>
      )
    }

    if (silentReauthError) {
      return renderTokenError('Unable to Complete Sign-In', silentReauthError)
    }

    if (!claimToken) {
      return renderTokenError(
        'Missing Claim Token',
        'No claim token was provided. Please use the link from your email or request a new login link.',
      )
    }

    if (error || !holeInOne) {
      return renderTokenError(
        'Invalid or Expired Link',
        'This claim link may have expired or is invalid. Please return to login and request a new link.',
      )
    }

    if (holeInOne.legalAgreementsAcceptedAt) {
      return (
        <Box className={classes.loadingContainer} tabIndex={-1} ref={statusRef}>
          <CircularProgress size={40} />
          <Typography className={classes.loadingText}>
            Signing you in...
          </Typography>
        </Box>
      )
    }

    if (hasEmailOnRecord) {
      return (
        <Box className={classes.statusContainer} tabIndex={-1} ref={statusRef}>
          {renderKnownEmailFlow()}
        </Box>
      )
    }

    return (
      <Box className={classes.statusContainer} tabIndex={-1} ref={statusRef}>
        {renderQRScanFlow()}
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.logoContainer}>
        <PgaLogo className={classes.logo} aria-label="PGA of America" />
        <Box className={classes.taglineDivider} />
        <Typography className={classes.tagline}>We love this Game</Typography>
      </Box>

      <Box className={classes.card}>{renderContent()}</Box>

      <Typography className={classes.footer}>
        © {new Date().getFullYear()} PGA of America. All rights reserved.
      </Typography>
    </Box>
  )
}

export default AchievementsClaimPage
